import { memo } from 'react';
import { prefetchByPath } from '../../routes/lazyRoutes';

/**
 * SidebarNavItem — single primary/more-section nav button used by Sidebar.
 *
 * Renders the icon disc, label/note when expanded, and a count badge
 * (compact when collapsed, inline when expanded). Crimson when urgent.
 *
 * Pure presentational. Active styling derives from `data-active` so the
 * existing `.pd-sidebar-nav-item.active` class can still target it.
 *
 * Wrapped in `React.memo` because Sidebar re-renders this list on every
 * count-payload tick — without memo, all ~14 nav items repaint every
 * 30s. The default shallow comparator is sufficient: `item` is a stable
 * module-level constant, `onClick` is `useCallback`-stabilised in the
 * parent.
 *
 * Also prefetches the route's lazy chunk on `onMouseEnter` (desktop)
 * and `onFocus` (keyboard) so clicking the link feels instant — the
 * import promise has already resolved by the time the click lands.
 */
function SidebarNavItem({ item, active, expanded, count, isUrgent, onClick }) {
  const Icon = item.icon;
  const handlePrefetch = () => prefetchByPath(item.path);
  return (
    <button
      type="button"
      data-testid={`nav-${item.label.toLowerCase()}`}
      onClick={onClick}
      onMouseEnter={handlePrefetch}
      onFocus={handlePrefetch}
      onTouchStart={handlePrefetch}
      className={`sidebar-nav-item pd-sidebar-nav-item ${active ? 'active' : ''}`}
      style={{
        minHeight: 48,
        justifyContent: expanded ? 'flex-start' : 'center',
        padding: expanded ? '0 10px' : '0',
      }}
    >
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 relative"
        style={{ background: active ? 'var(--pd-panel)' : 'transparent' }}
      >
        <Icon className="w-[17px] h-[17px]" strokeWidth={active ? 2.1 : 1.9} />
        {!expanded && count > 0 ? (
          <span
            data-testid={`nav-badge-${item.label.toLowerCase()}`}
            className="absolute -top-1 -right-1 min-w-[16px] h-[16px] rounded-full flex items-center justify-center text-[8px] font-bold text-white px-0.5"
            style={{ background: isUrgent ? 'var(--pd-crimson)' : 'var(--pd-focus)', lineHeight: 1 }}
          >
            {count > 99 ? '99+' : count}
          </span>
        ) : null}
      </div>
      {expanded ? (
        <>
          <div className="sidebar-label flex-1 min-w-0 text-left">
            <div className="text-[12px] font-semibold">{item.label}</div>
            <div
              className="text-[9px] mt-0.5"
              style={{ color: active ? 'var(--pd-muted)' : 'var(--pd-subtle)' }}
            >
              {item.note}
            </div>
          </div>
          {count > 0 ? (
            <span
              data-testid={`nav-badge-${item.label.toLowerCase()}`}
              className="min-w-[20px] h-[20px] rounded-full flex items-center justify-center text-[9px] font-bold text-white px-1 flex-shrink-0"
              style={{ background: isUrgent ? 'var(--pd-crimson)' : 'var(--pd-focus)' }}
            >
              {count > 99 ? '99+' : count}
            </span>
          ) : null}
        </>
      ) : null}
    </button>
  );
}

export default memo(SidebarNavItem);
